import React from "react";
import { Opportunity } from "@/opportunity/opportunity";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";
import { Card } from "@/components/ui/card";
import { useLocation } from "@/location/api";
import StaffOnly from "@/user/StaffOnly";
import { InstallationTile, SublocationMap } from "@/sublocation/SublocationCard";
import { HighlightAssetContextProvider } from "@/sublocation/HighlightAssetContext";


const OpportunitySublocationMapCard = ({ 
  opportunity, 
  defaultOpen = false,
  collapsible = true
}: { 
  opportunity: Opportunity;
  defaultOpen?: boolean;
  collapsible?: boolean;
}) => {

  const { data: location } = useLocation(opportunity.location.id);

  const sublocation = location.sublocations.find(sublocation => sublocation.name === opportunity.installation.sublocation)

  if (!sublocation) {
    const errorContent = (
      <div className="flex justify-center items-center p-8">
        <p className="text-gray-500 text-sm">Sublocation "{opportunity.installation.sublocation}" not found for this location.</p>
      </div>
    );

    if (!collapsible) {
      return (
        <Card className="p-3 sm:p-4 flex flex-col gap-2">
          {errorContent}
        </Card>
      );
    }

    return (
      <CollapsibleCard title="Location" defaultOpen={defaultOpen} collapsible={collapsible}>
        {errorContent}
      </CollapsibleCard>
    );
  }

  const installationsWithPositions = sublocation.installations.filter(installation => !!installation.position);

  const content = (
    <HighlightAssetContextProvider assetToHighlight={opportunity.installation}>
      <div className="flex justify-center bg-gray-50 bg-[url('@/assets/hex_tile_light.png')] pointer-events-none">
        {
          installationsWithPositions.length === 0 ? (
            <div className="bg-white m-8 p-8 flex flex-col items-center gap-2">
              <div className="grid grid-cols-4 gap-2">
                {
                  sublocation.installations.map(installation => (
                    <InstallationTile key={installation.id} installation={installation} disableTooltip={true}/>
                  ))
                }
              </div>
              <StaffOnly>
                <span
                  className="text-gray-400">Site map for this area is not yet available - configure in ThaloOS</span>
              </StaffOnly>
            </div>
          ) : (
            <div className="w-fit h-fit bg-white bg-opacity-80 m-8 p-8">
              <SublocationMap installations={installationsWithPositions} disableTooltip={true}/>
            </div>
          )
        }
      </div>
    </HighlightAssetContextProvider>
  );

  if (!collapsible) {
    return (
      <Card className="p-3 sm:p-4 flex flex-col gap-2">
        {content}
      </Card>
    );
  }

  return (
    <CollapsibleCard title="Location" defaultOpen={defaultOpen} collapsible={collapsible}>
      {content}
    </CollapsibleCard>
  );
};

export default OpportunitySublocationMapCard;